// src/components/AdminAnnouncements/AdminAnnouncementsEdit.js
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  FilledInput,
} from "@mui/material";

const AdminAnnouncementsEdit = ({ open, onClose, announcementData }) => {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [expireDate, setExpireDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "info" });

  useEffect(() => {
    if (announcementData) {
      setSubject(announcementData.announcement || "");
      setDescription(announcementData.description || "");
      setStartDate(announcementData.startDate || "");
      setExpireDate(announcementData.expireDate || "");
    }
  }, [announcementData]);

  const handleSubmit = async () => {
    // 1) Validate subject
    if (!subject.trim()) {
      setSnack({
        open: true,
        msg: "Subject is required.",
        sev: "error",
      });
      return;
    }

    if (!subject.trim() || !description.trim()) {
      setSnack({
        open: true,
        msg: "Subject and description are required.",
        sev: "error",
      });
      return;
    }

    if (description && description.length > 1000) {
      setSnack({
        open: true,
        msg: "Description cannot exceed 1,000 characters.",
        sev: "error",
      });
      return;
    }

    if (!expireDate) {
      setSnack({
        open: true,
        msg: "Expire date is required.",
        sev: "error",
      });
      return;
    }
  
    // Helper to parse a "YYYY-MM-DD" string into a Date at local midnight
    const parseLocalDate = (dateString) => {
      if (!dateString) return null;
      // "T00:00:00" ensures the date is treated as midnight in the user’s local timezone
      return new Date(`${dateString}T00:00:00`);
    };
  
    // Make a "today" date at local midnight for comparisons
    const today = new Date();
    today.setHours(0, 0, 0, 0);
  
    // Convert startDate and expireDate to local-midnight Date objects
    const sDate = parseLocalDate(startDate);
    const eDate = parseLocalDate(expireDate);
  
    
  
    // // 3) Validate that the expireDate is not before "today"
    // if (eDate && eDate < today) {
    //   setSnack({
    //     open: true,
    //     msg: "Expire date cannot be in the past.",
    //     sev: "error",
    //   });
    //   return;
    // }
  
    // 4) Validate that expireDate is on or after startDate
    if (sDate && eDate && eDate < sDate) {
      setSnack({
        open: true,
        msg: "Expire date must be after start date.",
        sev: "error",
      });
      return;
    }
  
    // If we pass all validations, submit the changes
    setSubmitting(true);
    try {
      const res = await axios.post("/admin/editAnnouncement", {
        announcementId: announcementData.announcementId,
        announcement: subject,
        description,
        startDate,
        expireDate,
      });
      if (res.data.success) {
        setSnack({
          open: true,
          msg: "Announcement updated.",
          sev: "success",
        });
        onClose(true);
      } else {
        setSnack({
          open: true,
          msg: res.data.message || "Failed to update announcement.",
          sev: "error",
        });
      }
    } catch (err) {
      setSnack({
        open: true,
        msg: "Server error updating announcement.",
        sev: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  

  return (
    <>
      <Dialog open={open} onClose={() => onClose(false)} maxWidth={false}>
        <DialogTitle className="mx-auto text-2xl font-bold font-brand-primary">
          EDIT ANNOUNCEMENT
        </DialogTitle>
        <DialogContent className="flex flex-col gap-4 mb-4 mx-4">
          {/* Subject */}
          <div className="flex flex-col w-96">
            <label className="text-lg font-brand-primary mb-1">Subject *</label>
            <FilledInput
              type="text"
              disableUnderline
              placeholder="Announcement Subject"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="rounded-xl bg-white border border-gray-300 h-11 px-3 [&>input]:pt-1"
              maxLength={100}
            />
          </div>
          {/* Description */}
          <div className="flex flex-col w-96">
            <label className="text-lg font-brand-primary mb-1">
              Announcement *
            </label>
            <FilledInput
              multiline
              minRows={4}
              disableUnderline
              placeholder="Full announcement text"
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="rounded-xl bg-white border border-gray-300 px-3 py-2"
              maxLength={1000}
            />
            <p className="helperText">
              {"You can use curly brackets {} to indicate Links/URLs e.g. {https://jobhub.cs.vt.edu} becomes "}<a href="https://jobhub.cs.vt.edu" target="_blank" rel="noopener noreferrer">https://jobhub.cs.vt.edu</a>.<br/>
              {"Additionally you can have the link appear as other text by proceeding it with your text and a bar symbol e.g. {link|https://jobhub.cs.vt.edu} becomes "}<a href="https://jobhub.cs.vt.edu" target="_blank" rel="noopener noreferrer">link</a>.<br/>
              {"Of note, all links will open in a new tab and be sure to include the 'https://' or 'http://' at the start of your links or they will not work."}
            </p>
          </div>
          {/* Start/Expire Dates */}
          <div className="flex justify-between w-96">
            <div className="flex flex-col w-44">
              <label className="text-lg font-brand-primary mb-1">
                Start Date
              </label>
              <input
                type="date"
                className="rounded-xl bg-white border border-gray-300 h-11 px-3"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="flex flex-col w-44">
              <label className="text-lg font-brand-primary mb-1">
                Expire Date
              </label>
              <input
                type="date"
                className="rounded-xl bg-white border border-gray-300 h-11 px-3"
                value={expireDate}
                onChange={(e) => setExpireDate(e.target.value)}
              />
            </div>
          </div>

        </DialogContent>
        {/* Footer Buttons */}
        <div className="flex justify-between px-8 mb-4">
          <button
            type="button"
            className="primary-button bg-brand-primary mb-2 h-11 pt-2 px-6"
            onClick={() => onClose(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="primary-button bg-brand-quaternary mb-2 h-11 pt-2 px-6"
            disabled={submitting}
            onClick={handleSubmit}>
            {submitting ? "Submitting..." : "Save Changes"}
          </button>
        </div>
      </Dialog>
      {/* Snackbar for Feedback */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert
          severity={snack.sev}
          onClose={() => setSnack({ ...snack, open: false })}
          sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

AdminAnnouncementsEdit.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired, // onClose(refresh:boolean)
  announcementData: PropTypes.shape({
    announcementId: PropTypes.number.isRequired,
    announcement: PropTypes.string,
    description: PropTypes.string,
    startDate: PropTypes.string,
    expireDate: PropTypes.string,
  }).isRequired,
};

export default AdminAnnouncementsEdit;
